import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import useEmployeeAnalysisText from './employeeAnalysisText';

const safeToString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).toLocaleLowerCase('tr-TR');
};

// Amounts stored as text use the Turkish format ("1.234,56")
const toAmount = (value) => (typeof value === 'string' ? Number(value.replace(/\./g, '').replace(',', '.')) : value);

// Excel keeps the old Turkish money format
const formatCurrencyTr = (amount) => {
  const numericAmount = typeof amount === 'number' ? amount : Number(amount);
  if (Number.isNaN(numericAmount)) return '0,00 ₺';
  return `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numericAmount)} ₺`;
};

/** Workers from Projects.employees (one entry per worker, their assignments inside). */
const buildWorkers = (projectsSnapshot) => {
  const workerMap = new Map();
  let projectsCount = 0;
  projectsSnapshot.docs.forEach((projectDoc) => {
    const projectData = projectDoc.data();
    const projectEmployees = projectData.employees || [];
    if (projectEmployees.length > 0) projectsCount++;
    projectEmployees.forEach((emp) => {
      // Basic validation
      if (!emp.id || !emp.name || !emp.surname) return;
      const projectInfo = {
        projectId: projectDoc.id,
        projectTitle: projectData.titleShort || 'N/A',
        role: emp.role,
        rate: emp.rate,
        assignedAt: emp.assignedAt, // Timestamp or Date
        centerName: emp.centerName,
      };
      if (workerMap.has(emp.id)) workerMap.get(emp.id).projects.push(projectInfo);
      else workerMap.set(emp.id, { id: emp.id, name: emp.name, surname: emp.surname, projects: [projectInfo] });
    });
  });
  return { workers: Array.from(workerMap.values()), projectsCount };
};

/**
 * Data of Çalışan Analizi: every worker with their project assignments,
 * visits / expenses / payments per assignment (all Visits, Salaries and
 * Payments read once, as before), the search and column filters (which
 * narrow a worker to the matching assignments), sorting, the Excel export
 * (with payment rows) and refreshing after an FTE change.
 */
const useEmployeeAnalysis = () => {
  const { userAuthorization } = useUserProfile();
  const { label } = useEmployeeAnalysisText();
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');

  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkDataLoading, setBulkDataLoading] = useState(false);
  const [activeProjects, setActiveProjects] = useState(0);
  const [visitCounts, setVisitCounts] = useState({});
  const [expenses, setExpenses] = useState({});
  const [payments, setPayments] = useState({});
  const [totalVisits, setTotalVisits] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);

  const resetBulk = () => {
    setVisitCounts({});
    setExpenses({});
    setPayments({});
    setTotalVisits(0);
    setTotalExpenses(0);
  };

  // Visits, expenses and payments per worker-project (all read once)
  const fetchBulkRelatedData = useCallback(async (workersData) => {
    if (!workersData || workersData.length === 0) {
      resetBulk();
      return;
    }
    setBulkDataLoading(true);
    try {
      const [visitsSnapshot, salariesSnapshot, paymentsSnapshot] = await Promise.all([
        getDocs(collection(db, 'Visits')),
        getDocs(collection(db, 'Salaries')),
        getDocs(collection(db, 'Payments')),
      ]);
      const visitCountMap = {};
      const expensesMap = {};
      const paymentsMap = {};
      let totalVisitCount = 0;
      let totalExpensesAmount = 0;
      const allVisits = visitsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const allSalaries = salariesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const allPayments = paymentsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      workersData.forEach((worker) => {
        worker.projects.forEach((project) => {
          const key = `${worker.id}-${project.projectId}`;

          // Visit count
          const visitsCount = allVisits.filter((visit) => visit.employeeId === worker.id && visit.projectId === project.projectId).length;
          visitCountMap[key] = visitsCount;
          totalVisitCount += visitsCount;

          // Expenses: salary payments (records without a project count for every project, as before) + other payments
          let projectExpense = 0;
          const workerSalaries = allSalaries.filter((salary) => salary.employeeId === worker.id);
          workerSalaries.forEach((salaryData) => {
            if (salaryData.payments && (!salaryData.projectId || salaryData.projectId === project.projectId)) {
              Object.values(salaryData.payments).forEach((payment) => {
                const amount = toAmount(payment.amount);
                if (!Number.isNaN(amount)) projectExpense += amount;
              });
            }
          });
          const workerProjectPayments = allPayments.filter((payment) => payment.employeeId === worker.id && payment.projectId === project.projectId);
          workerProjectPayments.forEach((paymentData) => {
            const amount = toAmount(paymentData.paidAmount);
            if (!Number.isNaN(amount)) projectExpense += amount;
          });
          expensesMap[key] = projectExpense;
          totalExpensesAmount += projectExpense;

          // Payment list (for the Excel file)
          const projectPaymentsAggregated = [];
          workerSalaries.forEach((salaryData) => {
            if (salaryData.payments && (!salaryData.projectId || salaryData.projectId === project.projectId)) {
              Object.entries(salaryData.payments).forEach(([paymentId, payment]) => {
                const amount = toAmount(payment.amount);
                if (!Number.isNaN(amount)) {
                  projectPaymentsAggregated.push({
                    id: paymentId,
                    paymentDate: payment.date,
                    paymentType: 'Maaş Ödemesi',
                    amount,
                    description: payment.description || 'Maaş Ödemesi',
                  });
                }
              });
            }
          });
          workerProjectPayments.forEach((paymentData) => {
            const amount = toAmount(paymentData.paidAmount);
            if (!Number.isNaN(amount)) {
              projectPaymentsAggregated.push({
                id: paymentData.id,
                paymentDate: paymentData.paymentDate,
                paymentType: paymentData.paymentType || 'Diğer Ödeme',
                amount,
                description: paymentData.description || '',
              });
            }
          });
          projectPaymentsAggregated.sort((a, b) => {
            const dateA = a.paymentDate?.toDate ? a.paymentDate.toDate() : new Date(a.paymentDate);
            const dateB = b.paymentDate?.toDate ? b.paymentDate.toDate() : new Date(b.paymentDate);
            if (Number.isNaN(dateA.getTime?.()) || Number.isNaN(dateB.getTime?.())) return 0;
            return dateB - dateA; // most recent first
          });
          paymentsMap[key] = projectPaymentsAggregated;
        });
      });

      setVisitCounts(visitCountMap);
      setExpenses(expensesMap);
      setPayments(paymentsMap);
      setTotalVisits(totalVisitCount);
      setTotalExpenses(totalExpensesAmount);
    } catch (error) {
      console.error('Error fetching bulk related data:', error);
      toast.error(label('toast.bulkError'));
      resetBulk();
    } finally {
      setBulkDataLoading(false);
    }
  }, [label]);

  useEffect(() => {
    const fetchAllWorkers = async () => {
      setLoading(true);
      try {
        const projectsSnapshot = await getDocs(collection(db, 'Projects'));
        const { workers: workersData, projectsCount } = buildWorkers(projectsSnapshot);
        setWorkers(workersData);
        setActiveProjects(projectsCount);
        setLoading(false);
        if (workersData.length > 0) fetchBulkRelatedData(workersData);
        else resetBulk();
      } catch (error) {
        console.error('Error fetching workers:', error);
        toast.error(label('toast.loadError'));
        setLoading(false);
        setBulkDataLoading(false);
      }
    };
    fetchAllWorkers();
    // Yalnızca açılışta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** After an FTE change: re-read the assignments (as before; totals stay). */
  const refreshWorkers = async () => {
    const loadingToast = toast.loading(label('toast.refreshing'));
    try {
      const projectsSnapshot = await getDocs(collection(db, 'Projects'));
      setWorkers(buildWorkers(projectsSnapshot).workers);
      toast.update(loadingToast, { render: label('toast.fteSaved'), type: 'success', isLoading: false, autoClose: 3000 });
    } catch (error) {
      console.error('Error refreshing workers:', error);
      toast.update(loadingToast, { render: label('toast.refreshError'), type: 'error', isLoading: false, autoClose: 3000 });
    }
  };

  const filteredWorkers = useMemo(() => {
    let filtered = [...workers];

    // Search: a matching name keeps all assignments, otherwise the matching projects / roles
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLocaleLowerCase('tr-TR').trim();
      filtered = filtered.map((worker) => {
        const workerName = `${worker.name || ''} ${worker.surname || ''}`.toLocaleLowerCase('tr-TR').trim();
        if (workerName.includes(lowercasedSearch)) return worker;
        const matchingProjects = worker.projects.filter((project) => safeToString(project.projectTitle).includes(lowercasedSearch) || safeToString(project.role).includes(lowercasedSearch));
        return matchingProjects.length > 0 ? { ...worker, projects: matchingProjects } : null;
      }).filter(Boolean);
    }

    // Column filters (narrow the worker to the matching assignments)
    Object.keys(filters).forEach((key) => {
      if (!filters[key]) return;
      filtered = filtered.map((worker) => {
        const matchingProjects = worker.projects.filter((project) => {
          switch (key) {
            case 'employee':
              return `${safeToString(worker.name)} ${safeToString(worker.surname)}`.includes(safeToString(filters[key]));
            case 'project':
              return safeToString(project.projectTitle).includes(safeToString(filters[key]));
            case 'role':
              return safeToString(project.role).includes(safeToString(filters[key]));
            default:
              return true;
          }
        });
        return matchingProjects.length > 0 ? { ...worker, projects: matchingProjects } : null;
      }).filter(Boolean);
    });

    return filtered;
  }, [workers, filters, searchTerm]);

  const getWorkerTotals = useCallback((worker) => worker.projects.reduce((acc, project) => {
    const key = `${worker.id}-${project.projectId}`;
    acc.visits += visitCounts[key] || 0;
    acc.expense += expenses[key] || 0;
    return acc;
  }, { visits: 0, expense: 0 }), [visitCounts, expenses]);

  const sortedWorkers = useMemo(() => {
    const arr = filteredWorkers.map((worker) => ({ ...worker, totals: getWorkerTotals(worker), roles: [...new Set(worker.projects.map((p) => p.role).filter(Boolean))] }));
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (sortConfig.key === 'name') return dir * `${a.name || ''} ${a.surname || ''}`.localeCompare(`${b.name || ''} ${b.surname || ''}`, 'tr');
      if (sortConfig.key === 'projects') return dir * (a.projects.length - b.projects.length);
      if (sortConfig.key === 'visits') return dir * (a.totals.visits - b.totals.visits);
      return dir * (a.totals.expense - b.totals.expense);
    });
    return arr;
  }, [filteredWorkers, sortConfig, getWorkerTotals]);

  // The panel shows the assignments the filters leave (as the expanded row did)
  const selectedWorker = useMemo(() => sortedWorkers.find((worker) => worker.id === selectedWorkerId)
    || (() => {
      const worker = workers.find((item) => item.id === selectedWorkerId);
      return worker ? { ...worker, totals: getWorkerTotals(worker), roles: [...new Set(worker.projects.map((p) => p.role).filter(Boolean))] } : null;
    })(), [sortedWorkers, workers, selectedWorkerId, getWorkerTotals]);

  const fullWorker = useCallback((workerId) => workers.find((worker) => worker.id === workerId) || null, [workers]);

  /** Sum of the worker's tariff values ("Tarife Toplamı"). */
  const calculateTotalRate = useCallback((workerId) => {
    const worker = workers.find((w) => w.id === workerId);
    if (!worker) return 0;
    let totalRate = 0;
    worker.projects.forEach((project) => {
      if (project.rate && project.rate.value) {
        const rateValue = typeof project.rate.value === 'string' ? parseFloat(project.rate.value.replace(/\./g, '').replace(',', '.')) : project.rate.value;
        if (!Number.isNaN(rateValue)) totalRate += rateValue;
      }
    });
    return totalRate;
  }, [workers]);

  const handleFilter = useCallback((column, value) => setFilters((current) => ({ ...current, [column]: value })), []);
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
  }, []);

  /** True when the worker has visits on the project (else a message). */
  const hasVisits = async (worker, project) => {
    try {
      const visitsSnapshot = await getDocs(query(collection(db, 'Visits'), where('employeeId', '==', worker.id), where('projectId', '==', project.projectId)));
      if (visitsSnapshot.empty) {
        toast.info(label('toast.noVisits'));
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error fetching employee visits:', error);
      toast.error(label('toast.visitsError'));
      return false;
    }
  };

  const downloadExcel = () => {
    const formatAssigned = (assignedAt) => assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') || (assignedAt instanceof Date ? assignedAt.toLocaleDateString('tr-TR') : '');
    const dataToExport = workers.flatMap((worker) => worker.projects.map((project) => {
      const key = `${worker.id}-${project.projectId}`;
      const projectPayments = payments[key] || [];
      const mainRow = {
        'Çalışan Adı': `${worker.name} ${worker.surname}`,
        'Proje': project.projectTitle,
        'Rol': project.role,
        'Tarife Tipi': isAdmin ? (project.rate?.type || '') : '',
        'Tarife Değeri': isAdmin ? (project.rate?.value || '') : '',
        'Atanma Tarihi': formatAssigned(project.assignedAt),
        'Visit Sayısı': visitCounts[key] || 0,
        'Toplam Gider': formatCurrencyTr(expenses[key] || 0),
      };
      if (projectPayments.length === 0) return [mainRow];
      return projectPayments.map((payment, index) => ({
        'Çalışan Adı': index === 0 ? `${worker.name} ${worker.surname}` : '',
        'Proje': index === 0 ? project.projectTitle : '',
        'Rol': index === 0 ? project.role : '',
        'Tarife Tipi': index === 0 && isAdmin ? (project.rate?.type || '') : '',
        'Tarife Değeri': index === 0 && isAdmin ? (project.rate?.value || '') : '',
        'Atanma Tarihi': index === 0 ? formatAssigned(project.assignedAt) : '',
        'Visit Sayısı': index === 0 ? (visitCounts[key] || 0) : '',
        'Toplam Gider': index === 0 ? formatCurrencyTr(expenses[key] || 0) : '',
        'Ödeme Tarihi': new Date(payment.paymentDate).toLocaleDateString('tr-TR'),
        'Ödeme Tipi': payment.paymentType,
        'Ödeme Tutarı': formatCurrencyTr(payment.amount),
        'Açıklama': payment.description,
      }));
    })).flat();

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    worksheet['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 30 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');
    XLSX.writeFile(workbook, 'Çalışan_Listesi.xlsx');
    toast.success(label('toast.excelDone'));
  };

  return {
    isAdmin,
    workers,
    loading,
    bulkDataLoading,
    activeProjects,
    visitCounts,
    expenses,
    totalVisits,
    totalExpenses,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    sortConfig,
    setSortConfig,
    sortedWorkers,
    selectedWorker,
    setSelectedWorkerId,
    fullWorker,
    calculateTotalRate,
    hasVisits,
    refreshWorkers,
    downloadExcel,
  };
};

export default useEmployeeAnalysis;
